import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, BookOpen, Award, Users, LayoutGrid, Settings,
  GraduationCap, ChevronDown, ChevronRight, FileText, Bot,
  Upload, Search, Star, MousePointer, Type, HelpCircle,
  Database, CheckSquare
} from 'lucide-react';

const Section = ({ icon: Icon, title, badge, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 bg-white hover:bg-slate-50 transition-colors text-left"
        data-testid={`help-section-${title.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4 text-primary" />
          </div>
          <span className="font-semibold text-sm">{title}</span>
          {badge && <Badge variant="secondary" className="text-xs">{badge}</Badge>}
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-5 pt-2 bg-white border-t space-y-3 text-sm text-muted-foreground">
          {children}
        </div>
      )}
    </div>
  );
};

const Step = ({ n, children }) => (
  <div className="flex gap-3 items-start">
    <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center font-bold">{n}</span>
    <p className="leading-relaxed">{children}</p>
  </div>
);

const Note = ({ children }) => (
  <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-xs leading-relaxed">
    <span className="font-semibold">Nota: </span>{children}
  </div>
);

const Tip = ({ children }) => (
  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-xs leading-relaxed">
    <span className="font-semibold">Consejo: </span>{children}
  </div>
);

// ─── Admin Sections ────────────────────────────────────────────
const AdminSections = () => (
  <div className="space-y-3">

    <Section icon={Users} title="Gestión de Usuarios" badge="Admin">
      <Step n="1">Ve a <strong>Dashboard → Gestionar Usuarios</strong> o dirígete a <code className="bg-slate-100 px-1 rounded">/admin/users</code>.</Step>
      <Step n="2">Usa el botón <strong>"+ Nuevo Usuario"</strong> para crear estudiantes, profesores o superadmins.</Step>
      <Step n="3">Completa el nombre, correo, contraseña y selecciona el <strong>rol</strong> apropiado.</Step>
      <Step n="4">Los usuarios con rol <strong>professor</strong> podrán acceder al editor de contenido de sus cursos.</Step>
      <Note>Solo los superadmins pueden crear otros superadmins.</Note>
    </Section>

    <Section icon={BookOpen} title="Gestión de Cursos">
      <Step n="1">Ve a <strong>Dashboard → Gestionar Cursos</strong> o <code className="bg-slate-100 px-1 rounded">/admin/courses</code>.</Step>
      <Step n="2">Crea un curso con el botón <strong>"+ Nuevo Curso"</strong>, asigna título, descripción, imagen y profesor.</Step>
      <Step n="3">Cambia el <strong>estado</strong> del curso entre <em>Borrador</em> y <em>Publicado</em> para controlar su visibilidad.</Step>
      <Step n="4">Usa el ícono de libro (contenido) para abrir el <strong>Editor de Contenido</strong> del curso.</Step>
    </Section>

    <Section icon={GraduationCap} title="Editor de Contenido LMS" badge="Nuevo">
      <Step n="1">Desde la lista de cursos, haz clic en el <strong>ícono de libro</strong> junto al curso.</Step>
      <Step n="2">Crea <strong>Módulos</strong> usando el botón "+ Agregar Módulo". Un módulo agrupa lecciones relacionadas.</Step>
      <Step n="3">Dentro de cada módulo, agrega <strong>Lecciones</strong> de tipo Video, Texto o Quiz.</Step>
      <Step n="4">Para lecciones de <strong>texto</strong>, usa el editor enriquecido con negritas, cursivas, listas, etc.</Step>
      <Step n="5">Para <strong>videos</strong>, pega una URL de YouTube o Vimeo.</Step>
      <Step n="6">Para <strong>quizzes</strong>, agrega preguntas con opciones múltiples y marca la respuesta correcta.</Step>
      <Step n="7">Arrastra los módulos o lecciones para <strong>reordenarlos</strong> (drag & drop).</Step>
      <Tip>Los estudiantes solo aprueban un quiz si obtienen 60% o más. El certificado se emite automáticamente al completar el 100% del curso.</Tip>
    </Section>

    <Section icon={Award} title="Certificados del Curso">
      <Step n="1">Ve a <strong>Dashboard → Emitir Certificados</strong> o <code className="bg-slate-100 px-1 rounded">/admin/certificates</code>.</Step>
      <Step n="2">En la pestaña <strong>"Certificados de Curso"</strong>, selecciona un estudiante, un curso y una plantilla.</Step>
      <Step n="3">El sistema genera automáticamente un código QR verificable y lo estampa en la plantilla.</Step>
      <Step n="4">Puedes <strong>revocar</strong> certificados desde la lista indicando el motivo.</Step>
      <Step n="5">Cualquier persona puede verificar un certificado en <code className="bg-slate-100 px-1 rounded">/verify</code> con el código o folio.</Step>
    </Section>

    <Section icon={Upload} title="Certificados Externos">
      <Step n="1">En la pestaña <strong>"Certificados Externos"</strong> de la página de certificados, haz clic en <strong>"Subir Certificado Externo"</strong>.</Step>
      <Step n="2">Sube una imagen JPG/PNG del certificado existente.</Step>
      <Step n="3">Completa los datos del propietario, curso y fecha.</Step>
      <Step n="4">Posiciona el <strong>QR verificable</strong> arrastrándolo a la posición correcta sobre la imagen.</Step>
      <Step n="5">Guarda para que el certificado quede disponible en la búsqueda pública.</Step>
    </Section>

    <Section icon={Search} title="Certificados Legacy (Importados)">
      <Step n="1">Los ~310 certificados importados desde el servidor externo ya están disponibles.</Step>
      <Step n="2">Los estudiantes pueden buscarlos por <strong>nombre</strong> o <strong>folio</strong> en <code className="bg-slate-100 px-1 rounded">/verify</code>.</Step>
      <Step n="3">Para volver a importar, ve al panel de certificados y usa el botón <strong>"Importar Legacy"</strong>.</Step>
      <Note>La importación puede tardar varios minutos dependiendo de la conexión al servidor externo.</Note>
    </Section>

    <Section icon={FileText} title="Plantillas de Certificado">
      <Step n="1">Ve a <strong>Dashboard → Plantillas</strong> o <code className="bg-slate-100 px-1 rounded">/admin/certificate-templates</code>.</Step>
      <Step n="2">Sube una imagen base (PNG/JPG) que usará como fondo del certificado.</Step>
      <Step n="3">Define los campos de texto: nombre, código, fecha y título del curso.</Step>
      <Step n="4">Ajusta la <strong>posición del QR</strong> (esquinas predefinidas o coordenadas exactas).</Step>
      <Step n="5">Selecciona esta plantilla al emitir certificados de curso.</Step>
    </Section>

    <Section icon={LayoutGrid} title="CMS – Editor de Páginas" badge="Nuevo">
      <Step n="1">Ve a <strong>Dashboard → Editor CMS</strong> o <code className="bg-slate-100 px-1 rounded">/admin/cms</code>.</Step>
      <Step n="2">Selecciona la página que deseas editar (p. ej. <em>Inicio, Nosotros, Contacto</em>).</Step>
      <Step n="3">Edita el <strong>título</strong>, <strong>subtítulo</strong> y el <strong>contenido</strong> principal de cada sección.</Step>
      <Step n="4">Haz clic en <strong>"Guardar"</strong> para publicar los cambios en tiempo real.</Step>
      <Tip>Los cambios se reflejan inmediatamente en la página pública sin necesidad de reiniciar el servidor.</Tip>
    </Section>

    <Section icon={Settings} title="Configuración SMTP" badge="Nuevo">
      <Step n="1">Ve a <strong>Dashboard → Configuración</strong> o <code className="bg-slate-100 px-1 rounded">/admin/settings</code>.</Step>
      <Step n="2">Ingresa los datos de tu proveedor de correo: host, puerto, usuario y contraseña.</Step>
      <Step n="3">Para <strong>Gmail</strong>: usa <code className="bg-slate-100 px-1 rounded">smtp.gmail.com</code>, puerto 587, y una "Contraseña de Aplicación".</Step>
      <Step n="4">Guarda la configuración y usa el botón <strong>"Enviar prueba"</strong> para verificar que funciona.</Step>
      <Note>Debes activar la verificación en dos pasos en Gmail para poder generar una contraseña de aplicación.</Note>
    </Section>

    <Section icon={Database} title="Backup del Sistema" badge="Nuevo">
      <Step n="1">Ve a <strong>Dashboard → Configuración</strong> o <code className="bg-slate-100 px-1 rounded">/admin/settings</code>.</Step>
      <Step n="2">Desplázate hasta la sección <strong>"Respaldo del Sistema"</strong>.</Step>
      <Step n="3">Haz clic en <strong>"Descargar Backup"</strong>. Se generará un archivo ZIP.</Step>
      <Step n="4">El ZIP contiene todas las colecciones de la base de datos en formato JSON, más los archivos subidos.</Step>
      <Note>El backup es solo para superadmins. Se recomienda hacer un backup periódico antes de cambios importantes.</Note>
    </Section>

    <Section icon={Bot} title="AI Tutor – Base de Conocimiento">
      <Step n="1">Ve a <strong>Dashboard → AI Tutor - Conocimiento</strong>.</Step>
      <Step n="2">Agrega artículos, guías o FAQs que el tutor usará para responder preguntas de los estudiantes.</Step>
      <Step n="3">El tutor combina este conocimiento con el contenido del curso activo para dar respuestas contextuales.</Step>
    </Section>

  </div>
);

// ─── Professor Sections ────────────────────────────────────────
const ProfessorSections = () => (
  <div className="space-y-3">

    <Section icon={BookOpen} title="Ver Mis Cursos">
      <Step n="1">Al iniciar sesión, ve a tu <strong>Dashboard</strong>. Verás la lista de cursos que tienes asignados.</Step>
      <Step n="2">Usa el botón <strong>"Contenido"</strong> junto a cada curso para abrir el editor de contenido.</Step>
      <Step n="3">Solo ves y editas los cursos que te han sido asignados como instructor.</Step>
    </Section>

    <Section icon={GraduationCap} title="Crear Módulos y Lecciones" badge="LMS">
      <Step n="1">En el <strong>Editor de Contenido</strong>, haz clic en <strong>"+ Agregar Módulo"</strong> para crear una unidad temática.</Step>
      <Step n="2">Dentro de cada módulo, haz clic en <strong>"+ Agregar Lección"</strong> y elige el tipo.</Step>
      <Step n="3">
        Tipos de lección disponibles:
        <ul className="mt-1 ml-4 list-disc space-y-0.5">
          <li><strong>Video:</strong> Pega una URL de YouTube o Vimeo</li>
          <li><strong>Texto:</strong> Escribe contenido con el editor enriquecido</li>
          <li><strong>Quiz:</strong> Crea un examen de opción múltiple</li>
        </ul>
      </Step>
      <Step n="4">Edita el nombre de cada módulo o lección haciendo clic en el ícono de lápiz.</Step>
      <Step n="5">Elimina lecciones o módulos usando el ícono de papelera (no se puede deshacer).</Step>
    </Section>

    <Section icon={Type} title="Editor de Texto Enriquecido (WYSIWYG)" badge="Nuevo">
      <Step n="1">Al crear o editar una lección de tipo <strong>Texto</strong>, verás la barra de herramientas del editor.</Step>
      <Step n="2">Usa los botones para aplicar <strong>negrita</strong>, <em>cursiva</em>, subrayado, etc.</Step>
      <Step n="3">Crea <strong>listas ordenadas o sin orden</strong> con los botones correspondientes.</Step>
      <Step n="4">Inserta <strong>bloques de código</strong> para contenido técnico.</Step>
      <Step n="5">El contenido se guarda en formato HTML enriquecido, que se muestra correctamente a los estudiantes.</Step>
      <Tip>Puedes copiar y pegar texto desde Word o Google Docs; el editor preservará el formato básico.</Tip>
    </Section>

    <Section icon={MousePointer} title="Reordenar con Drag & Drop" badge="Nuevo">
      <Step n="1">En el editor de contenido, verás un <strong>ícono de 6 puntos</strong> (⠿) al inicio de cada módulo y lección.</Step>
      <Step n="2">Haz clic y arrastra ese ícono para mover el módulo o lección a la posición deseada.</Step>
      <Step n="3">Suelta cuando estés en la posición correcta. El orden se guarda automáticamente.</Step>
      <Tip>El reordenamiento funciona por separado para módulos y para lecciones dentro de cada módulo.</Tip>
    </Section>

    <Section icon={CheckSquare} title="Crear Quizzes">
      <Step n="1">Al crear una lección de tipo <strong>Quiz</strong>, verás el constructor de preguntas.</Step>
      <Step n="2">Haz clic en <strong>"+ Agregar pregunta"</strong> para añadir cada pregunta.</Step>
      <Step n="3">Escribe la pregunta y las opciones de respuesta (mínimo 2, máximo 4).</Step>
      <Step n="4">Marca la <strong>respuesta correcta</strong> haciendo clic en la opción.</Step>
      <Step n="5">Los estudiantes necesitan <strong>60% o más</strong> para aprobar el quiz.</Step>
      <Note>Los estudiantes no ven cuál es la respuesta correcta durante el quiz; solo ven su puntuación al final.</Note>
    </Section>

    <Section icon={Star} title="Certificación Automática" badge="Nuevo">
      <Step n="1">Cuando un estudiante completa el <strong>100%</strong> de las lecciones del curso, el sistema le emite un certificado automáticamente.</Step>
      <Step n="2">El estudiante verá el certificado en su <strong>Dashboard</strong> bajo "Mis Certificados".</Step>
      <Step n="3">El certificado usa la <strong>plantilla</strong> configurada para el curso por el administrador.</Step>
      <Note>Si el curso no tiene plantilla asignada, el certificado se genera pero sin diseño personalizado. Pide al admin que asigne una plantilla.</Note>
    </Section>

  </div>
);

// ─── Main Component ────────────────────────────────────────────
const AdminHelp = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('admin');

  useEffect(() => {
    if (authLoading) return;
    if (!user || !['superadmin', 'admin', 'professor'].includes(user.role)) {
      navigate('/dashboard');
    }
    // Default tab based on role
    if (user?.role === 'professor') setTab('professor');
  }, [user, authLoading, navigate]);

  const isAdmin = user?.role !== 'professor';

  return (
    <div className="min-h-screen bg-secondary" data-testid="admin-help-page">
      <div className="max-w-3xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="gap-1 -ml-2 mb-3 text-muted-foreground">
              <ArrowLeft className="w-4 h-4" />Dashboard
            </Button>
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Centro de Ayuda</h1>
              <p className="text-sm text-muted-foreground">Guías paso a paso para usar la plataforma</p>
            </div>
          </div>
        </div>

        {/* Tab selector */}
        {isAdmin && (
          <div className="flex gap-2 mb-6 bg-white border rounded-xl p-1">
            <button
              onClick={() => setTab('admin')}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${tab === 'admin' ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="help-tab-admin"
            >
              Administradores
            </button>
            <button
              onClick={() => setTab('professor')}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${tab === 'professor' ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="help-tab-professor"
            >
              Profesores
            </button>
          </div>
        )}

        {/* Content */}
        {tab === 'admin' ? <AdminSections /> : <ProfessorSections />}

        {/* Footer */}
        <p className="mt-8 text-center text-xs text-muted-foreground">
          ¿Tienes dudas adicionales? Contacta al equipo técnico de IAASP Academy.
        </p>
      </div>
    </div>
  );
};

export default AdminHelp;
